import React from 'react';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../utils/api-service';
import { useDataStore, useUIStore } from '../context/store';
import RemindersPanel from './RemindersPanel';
import { ChangePasswordModal } from './ChangePasswordModal';
import headerLogo from '../assets/header-logo.jpeg';

export const Navbar: React.FC<{ onNotificationClick?: (notification: any) => void }> = ({ onNotificationClick }) => {
  const { user, logout } = useAuth();
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [userOpen, setUserOpen] = React.useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);
  const [remindersOpen, setRemindersOpen] = React.useState(false);
  const { isCRMMuted, toggleCRMMute } = useUIStore();
  const notifications = useDataStore((s) => s.notifications);
  const setNotifications = useDataStore((s) => s.setNotifications);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const notifRef = React.useRef<HTMLDivElement | null>(null);
  const userRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await notificationsAPI.list();
        if (mounted) setNotifications(res.data || []);
      } catch (err) {
        // ignore
      }
    };
    load();
    const id = window.setInterval(load, 30000);
    return () => {
      mounted = false;
      window.clearInterval(id);
    };
  }, [setNotifications]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (notifOpen && notifRef.current && !notifRef.current.contains(target)) {
        setNotifOpen(false);
      }

      if (userOpen && userRef.current && !userRef.current.contains(target)) {
        setUserOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen, userOpen]);

  if (!user) return null;

  return (
    <>
    <nav className="brand-header app-navbar border-b sticky top-0 z-10 md:pl-64">
      <div className="px-6 py-4 flex items-center w-full">
        <div className="flex items-center gap-4">
          <img src={headerLogo} alt="Hodophile logo" className="h-12 w-auto object-contain" />
        </div>

        <div className="flex items-center gap-6 ml-auto">
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setNotifOpen((v) => !v)}
              className="relative rounded-md p-2.5 text-slate-500 hover:bg-slate-100"
              aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
            >
              <span className="sr-only">Notifications</span>
              <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118.6 14.6V11a6 6 0 10-12 0v3.6c0 .538-.214 1.055-.595 1.422L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">{unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-700 rounded-lg shadow-lg z-20">
                <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-600 flex justify-between items-center">
                  <p className="text-sm font-medium">Notifications</p>
                  {unreadCount > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          await Promise.all(notifications.filter((n: any) => !n.is_read).map((n: any) => notificationsAPI.markRead(n.id)));
                          const res = await notificationsAPI.list();
                          setNotifications(res.data || []);
                        } catch (err) {}
                      }}
                      className="text-xs px-2 py-1 rounded bg-primary-500 text-white hover:bg-primary-600"
                    >
                      Mark All
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-auto">
                  {notifications.length === 0 && (
                    <div className="p-4 text-sm text-slate-500">No notifications</div>
                  )}
                  {notifications.map((n: any) => (
                    <div
                      key={n.id}
                      className={`px-4 py-3 border-b border-slate-100 dark:border-slate-600 ${n.is_read ? 'bg-white dark:bg-slate-700' : 'bg-slate-50 dark:bg-slate-800'}`}
                      onClick={() => {
                        if (onNotificationClick) onNotificationClick(n);
                      }}
                      style={{ cursor: onNotificationClick ? 'pointer' : 'default' }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{n.message}</p>
                          <p className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                        <div>
                          {!n.is_read && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await notificationsAPI.markRead(n.id);
                                  const res = await notificationsAPI.list();
                                  setNotifications(res.data || []);
                                } catch (err) {}
                              }}
                              className="text-xs px-2 py-1 rounded bg-primary-500 text-white"
                            >
                              Mark
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
            <div>
            <button 
              onClick={toggleCRMMute} 
              className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${
                isCRMMuted 
                  ? 'border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100' 
                  : 'border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
              aria-label={isCRMMuted ? 'CRM is muted' : 'Mute CRM alerts'}
              title={isCRMMuted ? 'CRM alerts are muted - click to unmute' : 'Click to mute CRM alerts'}
            >
              <span aria-hidden="true">{isCRMMuted ? '🔇' : '🔊'}</span><span className="ml-1.5 hidden sm:inline">{isCRMMuted ? 'Muted' : 'Mute'}</span>
            </button>
          </div>
            <div>
            <button onClick={() => setRemindersOpen(true)} className="rounded-md border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100" aria-label="Open reminders">
              <span aria-hidden="true">◷</span><span className="ml-1.5 hidden sm:inline">Reminders</span>
            </button>
          </div>
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen(!userOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[rgba(0,0,0,0.04)]"
            >
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm font-semibold navbar-user-name">{user.name}</span>
            </button>

            {userOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-700 rounded-lg shadow-lg z-20">
                <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-600">
                  <p className="text-sm font-medium">{user.email}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 capitalize">{user.role}</p>
                </div>
                {user.role === 'admin' && (
                  <button
                    onClick={() => {
                      setChangePasswordOpen(true);
                      setUserOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600"
                  >
                    Change Password
                  </button>
                )}
                <button
                  onClick={() => {
                    logout();
                    window.location.href = '/login';
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100 dark:hover:bg-slate-600"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
    {remindersOpen && <RemindersPanel onClose={() => setRemindersOpen(false)} />}
    <ChangePasswordModal isOpen={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </>
  );
};
